// ─────────────────────────────────────────────
//  useDayKey — the current LOCAL day key (YYYY-MM-DD), reactive across midnight.
//
//  rosterDateKey() read once at render is frozen for the life of the mount, so a
//  board left open overnight (the wall-mounted TV use case) keeps listening to
//  yesterday's roster/assignments forever. This hook re-renders the consumer at
//  the next LOCAL midnight (and reschedules), so every day-scoped listener that
//  depends on the returned key re-subscribes to the new day automatically.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { dayKey, msUntilNextDay } from "../domain/dayKey";
import { readTestMode } from "../domain/testMode";

// ⚠️ THE TIMER MUST FOLLOW THE SAME CLOCK AS THE KEY. It used to schedule against
// the DEVICE's midnight while the key is the FACILITY's day; on a board outside
// that zone the two disagree, and the wall display spends the gap listening to
// the wrong day — the exact window an overnight board is left unattended.
// `msUntilNextDay` walks forward to the first moment whose key differs, so DST
// (including the 23-hour spring-forward day) needs no special case.
export function useDayKey(tz) {
  // ⚠️ TESTING MODE PINS THE DAY, AND IT MUST NOT ROLL OVER. A session
  // replaying last Tuesday that advanced at midnight would silently start
  // asking for a day the snapshot does not contain, and every day-scoped
  // listener would return nothing — indistinguishable from an empty hospital.
  //
  // Read once per mount rather than at module scope: the value is a boot-time
  // decision, but reading it here keeps the hook testable without reaching
  // into module state.
  const [pinned] = useState(() => readTestMode()?.date || null);

  const [key, setKey] = useState(() => pinned || dayKey(new Date(), tz));

  useEffect(() => {
    if (pinned) return undefined; // a pinned past day never advances
    const t = setTimeout(() => setKey(dayKey(new Date(), tz)), msUntilNextDay(new Date(), tz));
    return () => clearTimeout(t);
  }, [key, tz, pinned]); // reschedule after each rollover

  return pinned || key;
}
